import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Auth } from 'aws-amplify';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { HttpErrorResponse, post } from '../../api/api-helpers';
import { useAppDispatch } from '../../app/hooks';
import { baseAddressSchema } from '../../common/components/address/select-address-helpers';
import { SelectAddress } from '../../common/components/address/SelectAddress';
import { CustomButton } from '../../common/components/CustomButton';
import { ModalProps } from '../../common/types/types';
import { fetchUserDetails } from '../user/userSlice';
import { ReactComponent as LogoDark } from './../../assets/images/clipsal_logo_dark.svg';
import { ReactComponent as Logo } from './../../assets/images/clipsal_logo.svg';

const initialState = {
  businessName: '',
  address: '',
  city: '',
  state: '',
  postCode: '',
  country: '',
};

type BusinessData = typeof initialState;

const schema = yup
  .object()
  .shape({
    businessName: yup.string().trim().required('*Business Name is required'),
    address: yup.string().trim().required('*Business Address is required'),
  })
  .concat(baseAddressSchema);

export const BusinessDetailsModal = ({ isOpen, onClose }: ModalProps) => {
  const methods = useForm<BusinessData>({ resolver: yupResolver(schema), defaultValues: initialState });
  const {
    register,
    handleSubmit: handleFormSubmit,
    watch,
    formState: { errors },
  } = methods;
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const [isFormFilled, setIsFormFilled] = useState(false);

  // Check if all fields are filled and enable submit button.
  // Using subscription so that we do not rerender whole component on value changes
  useEffect(() => {
    const subscription = watch((values) => {
      const isFormFilled = Object.values(values).every((value) => !!value);
      setIsFormFilled(isFormFilled);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    // Check if a user was deleted in cognito
    // To ensure users are not stuck on this modal due to cache issues
    const checkIfUserIsNotAuthenticated = async () => {
      try {
        await Auth.currentAuthenticatedUser({
          bypassCache: true,
        });
      } catch (error) {
        if (typeof error === 'string' && error === 'The user is not authenticated') {
          // reloading should remove any remaining cache and take user back to login page
          window.location.reload();
        }
      }
    };

    checkIfUserIsNotAuthenticated();
  }, [location]);

  const LogoIcon = useColorModeValue(Logo, LogoDark);

  async function handleSubmit(values: typeof initialState) {
    setIsLoading(true);

    try {
      await post('/fleet/complete_signup', {
        tenant_name: values.businessName,
        address: values.address,
        city: values.city,
        state: values.state,
        country: values.country,
        zipcode: values.postCode,
      });

      await dispatch(fetchUserDetails());
      setIsLoading(false);
      onClose();
      toast({ title: 'Successfully saved business details!', status: 'success', isClosable: true });
      navigate('/');
    } catch (error) {
      const toastConfig = {
        title: 'Something went wrong!',
        description: 'Please try again. If this issue persists, please contact support.',
      };

      const errorStatus = (error as HttpErrorResponse)?.response?.status;
      const errorType = (error as HttpErrorResponse)?.response?.data?.type;

      if (errorStatus === 403 && errorType === 'USER_ALREADY_EXISTS') {
        toastConfig.title = 'User already exists!';
        toastConfig.description = 'Please try resetting password to reset password, or contact us for support.';
      }

      toast({
        ...toastConfig,
        status: 'error',
        isClosable: true,
      });
      setIsLoading(false);
    }
  }

  return (
    <Modal
      scrollBehavior={'inside'}
      size={'sm'}
      isOpen={isOpen}
      onClose={() => undefined}
      isCentered
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent data-testid="business-details-modal" mx={5}>
        <ModalHeader>
          <Flex direction={'column'} align="center">
            <Box cursor={'pointer'} my={5} w={'60%'}>
              <LogoIcon />
            </Box>
            <Text fontSize={'md'}>Let's get started!</Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <FormProvider {...methods}>
            <Box onSubmit={handleFormSubmit(handleSubmit)} as={'form'} data-testid="business-details-form">
              <FormControl isInvalid={!!errors.businessName}>
                <FormLabel>Business name</FormLabel>
                <Input {...register('businessName')} placeholder="Enter business name" data-testid="business-name" />
                <FormErrorMessage>{errors.businessName?.message}</FormErrorMessage>
              </FormControl>

              <SelectAddress<BusinessData>
                testId="business-address"
                inputTitle="Business address"
                containerProps={{ mt: 4 }}
                predictionContainerProps={{ width: '100%' }}
              />

              <CustomButton
                data-testid="submit-business-details-button"
                isLoading={isLoading}
                loadingText={'Submitting...'}
                w="100%"
                maxW={320}
                rounded={3}
                py={6}
                mt={4}
                isDisabled={!isFormFilled}
              >
                Submit
              </CustomButton>
            </Box>
          </FormProvider>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
